import { FC, useState, useEffect } from "react";
import {
  Modal,
  Table,
  Button,
  notification as antNotification,
  Tag,
  Space,
  Typography,
  Badge,
  Dropdown,
  Menu,
  Empty,
  Grid,
  Image,
} from "antd";
import {
  BellOutlined,
  CheckOutlined,
  DeleteOutlined,
  MoreOutlined,
  FilePdfOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { DataService } from "../../../services/http-interceptor.service";
import moment from "moment";

const { Text, Title } = Typography;

interface Notification {
  id: string;
  _id: string;
  title: string;
  message: string;
  type: string;
  status: string;
  createdAt: string;
  metadata: {
    proyectName?: string;
    splitName?: string;
    taskType?: string;
    imageUrl?: string;
    attachments?: Array<{ name?: string; url: string; type?: string }>;
  };
}

interface Props {
  user: any;
}

const Notifications: FC<Props> = ({ user }) => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    if (user) {
      getNotifications();
      getUnreadCount();
    }
  }, [user]);

  const getNotifications = async (page: number = currentPage, limit: number = pageSize) => {
    try {
      setIsLoading(true);
      // Obtener todas las notificaciones (el badge ya muestra solo las no leídas)
      const response = await DataService.get(`notifications?page=${page}&limit=${limit}`);
      const data = response.data;

      setNotifications(data.results || []);
      setTotalResults(data.totalResults || 0);
      setTotalPages(data.totalPages || 0);
      setCurrentPage(data.page || page);
    } catch (error) {
      console.error('Error obteniendo notificaciones:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getUnreadCount = async () => {
    try {
      const response = await DataService.get('notifications/unread-count');
      setUnreadCount(response.data.count || 0);
    } catch (error) {
      console.error('Error obteniendo conteo de notificaciones:', error);
    }
  };

  // Función para manejar cambio de página
  const handlePageChange = (page: number, size?: number) => {
    const newPageSize = size || pageSize;
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
    getNotifications(page, newPageSize);
  };

  // Función para manejar cambio de tamaño de página
  const handlePageSizeChange = (current: number, size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset a la primera página
    getNotifications(1, size);
  };

  const markAsRead = async (notificationId: string) => {
    // Validar que el ID no sea undefined o vacío
    if (!notificationId || notificationId === 'undefined') {
      console.error('ID de notificación inválido:', notificationId);
      return;
    }

    try {
      console.log('Marcando como leída:', notificationId);
      await DataService.patch(`notifications/${notificationId}/read`);

      // Recargar las notificaciones para mantener la paginación correcta
      await getNotifications(currentPage, pageSize);

      setUnreadCount(prev => {
        const newCount = Math.max(0, prev - 1);
        console.log('Conteo no leídas actualizado:', prev, '->', newCount);
        return newCount;
      });
    } catch (error) {
      console.error('Error marcando notificación como leída:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await DataService.patch('notifications/mark-all-read');
      // Recargar las notificaciones para mantener la paginación correcta
      await getNotifications(currentPage, pageSize);
      setUnreadCount(0);
      antNotification.success({
        message: "Notificaciones",
        description: "Todas las notificaciones han sido marcadas como leídas",
        duration: 3,
      });
    } catch (error) {
      console.error('Error marcando todas las notificaciones como leídas:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    // Validar que el ID no sea undefined o vacío
    if (!notificationId || notificationId === 'undefined') {
      console.error('ID de notificación inválido:', notificationId);
      return;
    }

    try {
      console.log('Eliminando notificación:', notificationId);
      await DataService.delete(`notifications/${notificationId}`);

      // Recargar las notificaciones para mantener la paginación correcta
      await getNotifications(currentPage, pageSize);

      antNotification.success({
        message: "Notificación",
        description: "Notificación eliminada exitosamente",
        duration: 3,
      });
    } catch (error) {
      console.error('Error eliminando notificación:', error);
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'TASK_COMPLETED':
        return 'green';
      case 'TASK_ASSIGNED':
        return 'blue';
      case 'PROJECT_UPDATE':
        return 'orange';
      case 'PURCHASE_COMPLETED':
        return 'green';
      case 'TOOL_LOAN':
        return 'geekblue';
      case 'VEHICLE_SERVICE':
        return 'purple';
      case 'VEHICLE_DOC_EXPIRY':
        return 'volcano';
      case 'TOOL_PURCHASED':
        return 'gold';
      default:
        return 'default';
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case 'TASK_COMPLETED':
        return 'Tarea Completada';
      case 'TASK_ASSIGNED':
        return 'Tarea Asignada';
      case 'PROJECT_UPDATE':
        return 'Actualización de Proyecto';
      case 'PURCHASE_COMPLETED':
        return 'Realización de compra';
      case 'TOOL_LOAN':
        return 'Préstamo de herramientas';
      case 'VEHICLE_SERVICE':
        return 'Servicio de Vehículo';
      case 'VEHICLE_DOC_EXPIRY':
        return 'Documento de vehículo';
      case 'TOOL_PURCHASED':
        return 'Compra de herramienta';
      default:
        return type;
    }
  };

  const columns = [
    {
      title: 'Título',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      render: (text: string, record: Notification) => {
        const atts = record.metadata?.attachments?.length
          ? record.metadata.attachments
          : record.metadata?.imageUrl
            ? [{ name: 'Ticket', url: record.metadata.imageUrl, type: 'image/jpeg' }]
            : [];
        const isImage = (a: { url: string; type?: string }) =>
          String(a.type || '').startsWith('image')
          || /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(a.url || '');
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {atts.length > 0 && (
              <Image.PreviewGroup>
                {atts.map((a, i) => (
                  isImage(a) ? (
                    <Image
                      key={i}
                      src={a.url}
                      alt={a.name || 'adjunto'}
                      width={44}
                      height={44}
                      style={{ objectFit: 'cover', borderRadius: 6 }}
                    />
                  ) : (
                    <a
                      key={i}
                      href={a.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={a.name || 'Ver documento'}
                      style={{ fontSize: 20 }}
                    >
                      {String(a.type || '').includes('xml')
                        ? <FileTextOutlined />
                        : <FilePdfOutlined style={{ color: 'red' }} />}
                    </a>
                  )
                ))}
              </Image.PreviewGroup>
            )}
            <Text style={{
              color: '#262626',
              fontSize: '13px'
            }}>
              {text}
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Mensaje',
      dataIndex: 'message',
      key: 'message',
      width: 300,
      render: (text: string) => (
        <Text
          ellipsis={{ tooltip: text }}
          style={{
            maxWidth: 280,
            fontSize: '13px',
            color: '#595959'
          }}
        >
          {text}
        </Text>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: string) => (
        <Tag
          color={getTypeColor(type)}
          style={{
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 500
          }}
        >
          {getTypeText(type)}
        </Tag>
      ),
    },
    {
      title: 'Fecha',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => (
        <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
          <div>{moment(date).format('DD/MM/YYYY')}</div>
          <div>{moment(date).format('HH:mm')}</div>
        </div>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      render: (record: Notification) => (
        <Space size="small">
          {record.status === 'UNREAD' && (
            <Button
              size="small"
              type="text"
              icon={<CheckOutlined />}
              onClick={() => markAsRead(record._id)}
              style={{
                color: '#52c41a',
                fontSize: '12px'
              }}
            >
              Leída
            </Button>
          )}
          <Button
            size="small"
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteNotification(record.id)}
            style={{ fontSize: '12px' }}
          >
            Eliminar
          </Button>
        </Space>
      ),
    },
  ];

  const notificationMenu = (
    <Menu style={{ minWidth: 200 }}>
      <Menu.Item
        key="view-all"
        onClick={() => navigate('/admin/notifications')}
        icon={<BellOutlined />}
      >
        Ver todas las notificaciones
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      <Dropdown overlay={notificationMenu} trigger={['click']} placement="bottomRight">
        <Button
          type="text"
          icon={<BellOutlined />}
          style={{
            color: '#fff'
          }}
        >
          <Badge count={unreadCount} size="small">
            {screens.md && <span style={{ color: '#ffffff' }}>Notificaciones</span>}
          </Badge>
        </Button>
      </Dropdown>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BellOutlined style={{ color: '#1890ff' }} />
            <span>Notificaciones</span>
            {unreadCount > 0 && (
              <Badge count={unreadCount} size="small" />
            )}
          </div>
        }
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowModal(false)}>
            Cerrar
          </Button>,
        ]}
        width={900}
        style={{ top: 20 }}
      >
        {notifications.length === 0 ? (
          <Empty
            description="No hay notificaciones"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: '40px 0' }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={notifications}
            rowKey="_id"
            loading={isLoading}
            size="small"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalResults,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} de ${total} notificaciones`,
              size: 'small',
              onChange: handlePageChange,
              onShowSizeChange: handlePageSizeChange,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onRow={(record) => ({
              onClick: () => {
                if (record.status === 'UNREAD') {
                  markAsRead(record._id);
                }
              },
              style: {
                cursor: 'pointer',
                backgroundColor: 'transparent',
                borderLeft: '3px solid transparent',
              },
            })}
            scroll={{ x: 800 }}
          />
        )}
      </Modal>
    </>
  );
};

export default Notifications;
